import React, { useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import ClearIcon from '@mui/icons-material/Clear'
import { useDispatch, useSelector } from 'react-redux'
import { Collapse, Tooltip } from '@mui/material'
import { TEMPLATE_ITEMS_NAMES } from 'utils/constants'
import { pick } from 'ramda'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import { isNotNilOrEmpty } from 'utils/ramda'
import { checkAnalyticsCondition } from 'utils/analytics'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import EditGeneralInfo from 'features/bpDetails/Components/edit/EditGeneralInfo'
import EditAddress from 'features/bpDetails/Components/edit/EditAddress'
import EditBuildingTypes from 'features/bpDetails/Components/edit/EditBuildingTypes'
import AddAssignmentModal from 'features/accessControl/components/AddAssignmentModal'
import Modal from 'components/atoms/Modal'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { canEditBp } from 'utils/user'
import AddMultipleFilesModal from 'features/bpDetails/Components/files/AddMultipleFilesModal'
import { selectBpFilesList } from 'ducks/files/selectors'
import AlreadyUploadedFilesOfTypeModal from 'features/analytics/compontents/AlreadyUploadedFilesOfTypeModal'
import EditIcon from '@mui/icons-material/Edit'
import EditOffIcon from '@mui/icons-material/EditOff'
import PersonIcon from '@mui/icons-material/Person'
import PeopleIcon from '@mui/icons-material/People'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LaunchIcon from '@mui/icons-material/Launch'
import AttachTagsToUploadingFile from 'features/bpDetails/Components/files/AttachTagsToUploadingFiles'
import { getFileTagsForBpRoutine } from 'ducks/files/actions'
import { updateAttachedAnalyticsTemplateTagRequirementsRoutine } from 'features/analytics/ducks/actions'
import AssignChecklistItemModal from 'features/analytics/compontents/AssignChecklistItemModal'
import ChecklistItemTagsPreviewModal from 'features/analytics/compontents/ChecklistItemTagsPreviewModal'
import ChecklistOverviewSummary from 'features/analytics/compontents/ChecklistOverviewSummary'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined'
import { getTemplateProvenanceLabel } from 'features/analytics/utils/templateProvenance'
import { canCurrentUserDetachTemplateFromBp } from 'features/analytics/utils/templateProvenance'
import { scoreColour } from 'features/dashboard/charts/scoreColour'

const color = scoreColour

const getChecklistItemTagNames = item => {
  const requirements =
    item?.bpTagRequirements ||
    item?.bp_tag_requirements ||
    item?.tagRequirements ||
    item?.tag_requirements ||
    []

  return Array.from(
    new Set(
      requirements
        .map(
          tag =>
            tag?.fileTag?.name || tag?.file_tag?.name || tag?.tag_value || null
        )
        .filter(Boolean)
    )
  )
}

const getChecklistGroupNumbers = (item, overviewEntries = []) => {
  const groupNos = new Set()
  const requirements =
    item?.bp_tag_requirements ||
    item?.bpTagRequirements ||
    item?.tag_requirements ||
    item?.tagRequirements ||
    []
  const assignments = item?.assignments || []

  overviewEntries.forEach(entry => {
    const groupNo = Number(entry?.groupNo ?? entry?.group_no)
    if (!Number.isNaN(groupNo)) groupNos.add(groupNo)
  })

  requirements.forEach(requirement => {
    const groupNo = Number(requirement?.group_no ?? requirement?.groupNo)
    if (!Number.isNaN(groupNo)) groupNos.add(groupNo)
  })

  assignments.forEach(assignment => {
    const groupNo = Number(assignment?.group_no ?? assignment?.groupNo)
    if (!Number.isNaN(groupNo)) groupNos.add(groupNo)
  })

  return Array.from(groupNos).sort((a, b) => a - b)
}

const getChecklistProgress = (filesCount, totalRulesCount) => {
  if (totalRulesCount > 0) {
    return (Math.min(filesCount, totalRulesCount) / totalRulesCount) * 100
  }

  return filesCount > 0 ? 100 : 0
}

const getTemplatePercentage = template => {
  const rawPercentage =
    template?.percentageValue ??
    template?.percentage_value ??
    template?.pivot?.percentageValue ??
    template?.pivot?.percentage_value ??
    0

  const numericPercentage = Number(rawPercentage)
  if (!Number.isFinite(numericPercentage)) return 0

  return Math.max(0, Math.min(numericPercentage, 100))
}

const isChecklistEntryUploaded = entry => {
  const files = entry?.uploadedFiles || entry?.uploaded_files || []
  const taskStatus = String(entry?.task?.status || '').toLowerCase()

  return (
    Boolean(entry?.hasUploadedFile || entry?.has_uploaded_file) ||
    files.length > 0 ||
    taskStatus === 'completed'
  )
}

const appendIfPresent = (params, key, value) => {
  if (value === undefined || value === null || value === '') return
  params.append(key, value)
}

const getEntryAssigneeIds = entry =>
  [
    ...(entry?.assignee_user_ids || []),
    entry?.assignee_user_id,
    entry?.assigneeUserId,
    ...(entry?.assignees || []).map(assignee => assignee?.id),
    entry?.assignee?.id
  ]
    .filter(Boolean)
    .map(String)

const getAssigneeDisplayName = assignee =>
  assignee?.label ||
  [
    assignee?.firstName || assignee?.first_name,
    assignee?.lastName || assignee?.last_name
  ]
    .filter(Boolean)
    .join(' ') ||
  assignee?.email ||
  ''

const getEntryAssigneeUsers = entry =>
  [
    ...(entry?.assignees || []),
    entry?.assignee
  ]
    .filter(assignee => assignee?.id)
    .map(assignee => {
      const label = getAssigneeDisplayName(assignee)
      const [firstName, ...lastNameParts] = String(label || '').split(' ')

      return {
        id: String(assignee.id),
        firstName:
          assignee.firstName ||
          assignee.first_name ||
          firstName ||
          assignee.email ||
          'User',
        lastName:
          assignee.lastName ||
          assignee.last_name ||
          lastNameParts.join(' '),
        email: assignee.email || '',
        label
      }
    })

const mergeUniqueById = items =>
  Array.from(
    (items || []).reduce((acc, item) => {
      if (item?.id) acc.set(String(item.id), item)
      return acc
    }, new Map()).values()
  )

const getEntryUnit = entry => ({
  ...(entry?.unit || {}),
  id:
    entry?.unit?.id ||
    entry?.unit?.building_unit_passport_id ||
    entry?.unit?.buildingUnitPassportId ||
    entry?.building_unit_passport_id ||
    entry?.buildingUnitPassportId ||
    entry?.task?.building_unit_passport_id ||
    entry?.task?.buildingUnitPassportId ||
    ''
})

const getUnitId = unit =>
  String(
    unit?.id ||
      unit?.building_unit_passport_id ||
      unit?.buildingUnitPassportId ||
      ''
  )

const getUnitName = (unit, index) =>
  unit?.name ||
  unit?.unit_name ||
  unit?.unitName ||
  (unit?.id ? `Unit ${index + 1}` : '')

const hasBpTaskManagement = bp =>
  Boolean(
    bp?.hasTaskManagement ??
      bp?.has_task_management ??
      bp?.taskManagementEnabled ??
      bp?.task_management_enabled
  )

const ChecklistTaskTrigger = ({
  rules,
  bpId,
  bpName,
  categoryId,
  typeId,
  template,
  item,
  displayName,
  overviewEntries
}) => {
  const [open, setOpen] = useState(false)
  const [selectedRule, setSelectedRule] = useState(null)

  const entries = overviewEntries || []
  const templateId = template?.id
  const templateName = template?.name || ''
  const itemId = item?.id
  const itemTitle = item?.title || displayName
  const itemDescription = item?.description || ''

  const buildCreateTaskUrl = (rule = null, unit = null) => {
    const params = new URLSearchParams()
    const titleParts = ['Upload', itemTitle]
    const unitId = unit ? getUnitId(unit) : ''

    if (unit?.name) {
      titleParts.push(`(${unit.name})`)
    }

    appendIfPresent(params, 'checklist', '1')
    appendIfPresent(params, 'title', titleParts.join(' '))
    appendIfPresent(
      params,
      'description',
      [
        templateName ? `Assigned from checklist template "${templateName}".` : null,
        itemDescription ? itemDescription.trim() : null,
        'Upload the required file to complete this checklist item.'
      ].filter(Boolean).join('\n\n')
    )
    appendIfPresent(params, 'bpId', bpId)
    appendIfPresent(params, 'bpName', bpName)
    appendIfPresent(params, 'templateId', templateId)
    appendIfPresent(params, 'templateName', templateName)
    appendIfPresent(params, 'itemId', itemId)
    appendIfPresent(params, 'itemTitle', itemTitle)
    appendIfPresent(params, 'fileTypeId', typeId)
    appendIfPresent(params, 'fileCategoryId', categoryId)
    appendIfPresent(params, 'groupNo', rule?.groupNo ?? 0)
    appendIfPresent(params, 'assignmentScope', unitId ? 'unit' : 'bp')
    appendIfPresent(params, 'unitId', unitId)
    appendIfPresent(params, 'unitName', unit?.name)

    const tagIds = rule?.tagIds || []
    if (tagIds.length > 0) params.append('tagIds', tagIds.join(','))

    const assigneeIds = Array.from(
      new Set(unit?.assigneeIds?.length > 0 ? unit.assigneeIds : rule?.assigneeIds || [])
    )
    if (assigneeIds.length > 0) params.append('assigneeIds', assigneeIds.join(','))

    const assigneeUsers = mergeUniqueById(
      unit?.assigneeUsers?.length > 0 ? unit.assigneeUsers : rule?.assigneeUsers || []
    )
    if (assigneeUsers.length > 0) {
      params.append('assigneeUsers', JSON.stringify(assigneeUsers))
    }

    return `${PRIVATE_PATHS.createTask}?${params.toString()}`
  }

  const openCreateTask = (rule = null, unit = null) => {
    redirect(buildCreateTaskUrl(rule, unit))
  }

  const unitsForRule = useMemo(() => {
    if (!selectedRule) return []
    const groupNo = selectedRule.groupNo

    return entries
      .filter(entry => {
        const entryGroupNo = entry?.groupNo ?? entry?.group_no ?? 0
        return Number(entryGroupNo) === Number(groupNo) &&
          entry?.scope === 'unit' &&
          getUnitId(getEntryUnit(entry))
      })
      .map((entry, index) => {
        const unit = getEntryUnit(entry)

        return {
          id: getUnitId(unit),
          name: getUnitName(unit, index),
          assigneeIds: getEntryAssigneeIds(entry),
          assigneeUsers: getEntryAssigneeUsers(entry),
          assignee:
            entry?.assignee?.label ||
            entry?.assignee?.email ||
            (entry?.assignees || [])
              .map(assignee => assignee?.label || assignee?.email)
              .filter(Boolean)
              .join(', ') ||
            null
        }
      })
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  }, [entries, selectedRule])

  const handleRuleClick = rule => {
    const ruleEntries = entries.filter(entry => {
      const entryGroupNo = entry?.groupNo ?? entry?.group_no ?? 0
      return Number(entryGroupNo) === Number(rule.groupNo)
    })
    const unitEntries = ruleEntries.filter(
      entry => entry?.scope === 'unit' && getUnitId(getEntryUnit(entry))
    )

    if (unitEntries.length > 0) {
      setSelectedRule(rule)
      return
    }

    const bpEntry = ruleEntries.find(entry => entry?.scope !== 'unit')
    openCreateTask({
      ...rule,
      assigneeIds: getEntryAssigneeIds(bpEntry),
      assigneeUsers: getEntryAssigneeUsers(bpEntry)
    })
  }

  const handleClick = e => {
    e.stopPropagation()

    if (rules.length > 1) {
      setOpen(true)
      return
    }

    if (rules.length === 1) {
      handleRuleClick(rules[0])
      return
    }

    openCreateTask()
  }

  const handleClose = () => {
    setSelectedRule(null)
    setOpen(false)
  }

  return (
    <>
      <CreateTaskTrigger
        type='button'
        aria-label={`Create task for ${displayName}`}
        title='Create task'
        onClick={handleClick}
      >
        <CreateTaskIcon />
      </CreateTaskTrigger>
      {open && (
        <Modal
          open={open}
          title={selectedRule
            ? `Create task for ${itemTitle}`
            : `Create task for ${displayName}`
          }
          onClose={handleClose}
        >
          <RulePickerContent>
            {selectedRule ? (
              <>
                <RulePickerHint>
                  One file is required per unit. Choose the unit that should receive the task.
                </RulePickerHint>
                <BackButton type='button' onClick={() => setSelectedRule(null)}>
                  Choose another rule
                </BackButton>
                {unitsForRule.map((unit, index) => (
                  <RulePickerCard
                    key={unit.id}
                    type='button'
                    onClick={() => openCreateTask(selectedRule, unit)}
                  >
                    <RulePickerIndex>{index + 1}</RulePickerIndex>
                    <RulePickerInfo>
                      <RulePickerName>{unit.name}</RulePickerName>
                      {unit.assignee && (
                        <RulePickerState>{unit.assignee}</RulePickerState>
                      )}
                    </RulePickerInfo>
                    <CreateTaskIcon />
                  </RulePickerCard>
                ))}
                {unitsForRule.length === 0 && (
                  <RulePickerHint>
                    No units are assigned to this rule. Use Settings to update the checklist assignments.
                  </RulePickerHint>
                )}
              </>
            ) : (
              <>
                <RulePickerHint>
                  Choose whether the upload requires one file for the whole Building Passport or one file per unit (select below). You can create multiple rules for the same file upload—for example, one Asbestos Survey tagged “South Block” and another tagged “North Block”. Choose which rule the task should follow.
                </RulePickerHint>
                {rules.map((rule, index) => (
                  <RulePickerCard
                    key={rule.groupNo}
                    type='button'
                    onClick={() => handleRuleClick(rule)}
                  >
                    <RulePickerIndex>{index + 1}</RulePickerIndex>
                    <RulePickerInfo>
                      <RulePickerName>
                        {rule.ruleName || `Rule ${index + 1}`}
                      </RulePickerName>
                      {rule.tagNames.length > 0 && (
                        <RulePickerTags>
                          {rule.tagNames.map(tag => (
                            <RulePickerTag key={tag}>{tag}</RulePickerTag>
                          ))}
                        </RulePickerTags>
                      )}
                    </RulePickerInfo>
                    <CreateTaskIcon />
                  </RulePickerCard>
                ))}
              </>
            )}
          </RulePickerContent>
        </Modal>
      )}
    </>
  )
}

const RulePickerUpload = ({ rules, bpId, categoryId, typeId, templateId, filesCount, displayName, overviewEntries }) => {
  const [open, setOpen] = useState(false)
  const [selectedRule, setSelectedRule] = useState(null)

  const unitsForRule = useMemo(() => {
    if (!selectedRule || !overviewEntries) return []
    const gNo = selectedRule.groupNo
    return overviewEntries
      .filter(e => {
        const entryGroupNo = e?.groupNo ?? e?.group_no ?? 0
        return Number(entryGroupNo) === Number(gNo) && e?.scope === 'unit' && e?.unit?.id
      })
      .map(e => ({
        id: e.unit.id,
        name: e.unit.name || `Unit ${e.unit.id}`,
        uploaded: !!(e?.hasUploadedFile || e?.has_uploaded_file),
        assignee: e?.assignee?.label || e?.assignee?.email || null
      }))
  }, [selectedRule, overviewEntries])

  const hasBpScope = useMemo(() => {
    if (!selectedRule || !overviewEntries) return false
    const gNo = selectedRule.groupNo
    return overviewEntries.some(e => {
      const entryGroupNo = e?.groupNo ?? e?.group_no ?? 0
      return Number(entryGroupNo) === Number(gNo) && e?.scope !== 'unit'
    })
  }, [selectedRule, overviewEntries])

  const handleRuleClick = (rule) => {
    if (rule.disabled) return

    const gNo = rule.groupNo
    const ruleUnitEntries = (overviewEntries || []).filter(e => {
      const entryGroupNo = e?.groupNo ?? e?.group_no ?? 0
      return Number(entryGroupNo) === Number(gNo) && e?.scope === 'unit' && e?.unit?.id
    })

    if (ruleUnitEntries.length > 0) {
      setSelectedRule(rule)
    } else {
      openUploadPage(rule, null)
    }
  }

  const openUploadPage = (rule, unitId) => {
    const params = new URLSearchParams({ method: 'traditional' })
    if (categoryId) params.append('category', categoryId)
    if (typeId) params.append('type', typeId)
    if (templateId) params.append('templateId', templateId)
    if (rule.tagIds && rule.tagIds.length > 0) {
      params.append('tags', rule.tagIds.join(','))
    }
    if (unitId) params.append('unit', unitId)
    window.open(`/files/upload/${bpId}?${params.toString()}`, '_blank')
    setSelectedRule(null)
    setOpen(false)
  }

  const handleBack = () => setSelectedRule(null)
  const handleClose = () => { setSelectedRule(null); setOpen(false) }

  return (
    <>
      <AddFilesTrigger
        type='button'
        aria-label={`Add files for ${displayName}`}
        title='Add files'
        onClick={e => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        <NoteAddOutlinedIcon style={{ fontSize: 18 }} />
        <span>Add files</span>
      </AddFilesTrigger>
      {open && (
        <Modal
          open={open}
          title={selectedRule
            ? `Select unit for ${selectedRule.ruleName || 'this rule'}`
            : `Upload files for ${displayName}`
          }
          onClose={handleClose}
        >
          <RulePickerContent>
            {selectedRule ? (
              <>
                <RulePickerHint>
                  Select which unit you want to upload files for.
                </RulePickerHint>
                <BackButton type='button' onClick={handleBack}>
                  ← Back to rules
                </BackButton>
                {hasBpScope && (
                  <RulePickerCard
                    type='button'
                    onClick={() => openUploadPage(selectedRule, null)}
                  >
                    <RulePickerIndex>BP</RulePickerIndex>
                    <RulePickerInfo>
                      <RulePickerName>Whole Building Passport</RulePickerName>
                    </RulePickerInfo>
                    <NoteAddOutlinedIcon style={{ fontSize: 18, color: '#33becc' }} />
                  </RulePickerCard>
                )}
                {unitsForRule.map(unit => (
                  <RulePickerCard
                    key={unit.id}
                    type='button'
                    onClick={() => openUploadPage(selectedRule, unit.id)}
                    disabled={unit.uploaded}
                  >
                    <RulePickerIndex>
                      {unit.uploaded ? '✓' : '↑'}
                    </RulePickerIndex>
                    <RulePickerInfo>
                      <RulePickerName>{unit.name}</RulePickerName>
                      {unit.assignee && (
                        <RulePickerState>{unit.assignee}</RulePickerState>
                      )}
                    </RulePickerInfo>
                    {unit.uploaded ? (
                      <RuleUploadedIndicator>Uploaded</RuleUploadedIndicator>
                    ) : (
                      <NoteAddOutlinedIcon style={{ fontSize: 18, color: '#33becc' }} />
                    )}
                  </RulePickerCard>
                ))}
              </>
            ) : (
              <>
                <RulePickerHint>
                  Select which rule you want to upload files for. Each rule has different tag requirements.
                </RulePickerHint>
                {rules.map((rule, index) => (
                  <RulePickerCard
                    key={rule.groupNo}
                    type='button'
                    onClick={() => handleRuleClick(rule)}
                    disabled={rule.disabled}
                  >
                    <RulePickerIndex>{index + 1}</RulePickerIndex>
                    <RulePickerInfo>
                      <RulePickerName>
                        {rule.ruleName || `Rule ${index + 1}`}
                      </RulePickerName>
                      {rule.tagNames.length > 0 && (
                        <RulePickerTags>
                          {rule.tagNames.map(tag => (
                            <RulePickerTag key={tag}>{tag}</RulePickerTag>
                          ))}
                        </RulePickerTags>
                      )}
                      {rule.disabled && (
                        <RulePickerState>
                          <RuleUploadedBadge>File already uploaded</RuleUploadedBadge>
                        </RulePickerState>
                      )}
                    </RulePickerInfo>
                    {rule.disabled ? (
                      <RuleUploadedIndicator>Uploaded</RuleUploadedIndicator>
                    ) : (
                      <NoteAddOutlinedIcon style={{ fontSize: 18, color: '#33becc' }} />
                    )}
                  </RulePickerCard>
                ))}
              </>
            )}
          </RulePickerContent>
        </Modal>
      )}
    </>
  )
}

const AnalyticProgressBar = ({
  template,
  bp,
  isDetailsView,
  handleOpenRemoveModal,
  currentPage,
  showDetailsOnly = false,
  showOverviewButton = false
}) => {
  const isDefaultTemplate = template.type === 'default'
  const dispatch = useDispatch()
  const fileCategories = useSelector(selectFileCategories)
  const [open, setOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editContentName, setEditContentName] = useState('')
  const user = useSelector(getCurrentUser)
  const canEdit = canEditBp(user, bp)
  const creatorId =
    template?.creator_id ||
    template?.creatorId ||
    template?.creator?.id ||
    template?.created_by?.id ||
    template?.createdBy?.id ||
    template?.user_id ||
    template?.userId
  const isCreator =
    creatorId && user?.id ? String(creatorId) === String(user.id) : false
  const isAdmin = user?.type === 'admin'
  const rawLocked =
    template?.locked ??
    template?.is_locked ??
    template?.isLocked ??
    template?.pivot?.locked
  const hasLockState = typeof rawLocked === 'boolean'
  const isLocked = !!rawLocked
  const canEditTemplate = isCreator || isAdmin || !isLocked
  const canDetachTemplate = canCurrentUserDetachTemplateFromBp(template, user, bp)
  const isTemplateEditable =
    canEdit && canEditTemplate && template.type !== 'default'
  const canUploadFiles = canEdit && template.type !== 'default'
  const bpFiles = useSelector(selectBpFilesList)
  const [fileItemTagSelections, setFileItemTagSelections] = useState({})
  const [itemOverrides, setItemOverrides] = useState({})
  const templatePercentage = getTemplatePercentage(template)

  useEffect(() => {
    setItemOverrides({})
  }, [template?.id])

  const checklistFileItemsByType = useMemo(() => {
    const templateItems =
      template?.items ||
      template?.analyticsTemplateItems ||
      template?.analytics_template_items ||
      []

    return templateItems
      .filter(item => (item.itemType || item.item_type || 'file') === 'file')
      .reduce((acc, item) => {
        const fileTypeId = item.fileTypeId || item.file_type_id
        if (!fileTypeId || acc[String(fileTypeId)]) return acc
        acc[String(fileTypeId)] = itemOverrides[item.id] || itemOverrides[String(fileTypeId)] || item
        return acc
      }, {})
  }, [itemOverrides, template])

  useEffect(() => {
    const templateItems =
      template?.items ||
      template?.analyticsTemplateItems ||
      template?.analytics_template_items ||
      []

    const itemSelections = templateItems
      .filter(item => (item.itemType || item.item_type || 'file') === 'file')
      .reduce((acc, item) => {
        const fileTypeId = item.fileTypeId || item.file_type_id
        const tagIds = (
          item.bpTagRequirements ||
          item.bp_tag_requirements ||
          item.tagRequirements ||
          item.tag_requirements ||
          []
        )
          .map(
            tag =>
              tag?.fileTagId ||
              tag?.file_tag_id ||
              tag?.file_tag?.id ||
              tag?.fileTag?.id
          )
          .filter(Boolean)
        if (!fileTypeId) return acc
        const key = String(fileTypeId)
        acc[key] = Array.from(
          new Set([...(acc[key] || []), ...tagIds.map(String)])
        )
        return acc
      }, {})

    const fallbackByFileType =
      template?.bp_item_tag_requirements_by_file_type ||
      template?.bpItemTagRequirementsByFileType ||
      {}

    const requirementsByFileType = (
      template?.item_tag_requirements || []
    ).reduce((acc, requirement) => {
      const fileTypeId = requirement?.file_type_id || requirement?.fileTypeId
      const tagIds = requirement?.file_tag_ids || requirement?.fileTagIds || []
      if (!fileTypeId) return acc
      const key = String(fileTypeId)
      acc[key] = Array.from(
        new Set([...(acc[key] || []), ...tagIds.map(String)])
      )
      return acc
    }, {})

    const merged = {
      ...fallbackByFileType,
      ...requirementsByFileType,
      ...itemSelections
    }

    setFileItemTagSelections(merged)
  }, [template])

  const ensureBpTagsLoaded = e => {
    e.stopPropagation()
    if (bp?.id) {
      dispatch(getFileTagsForBpRoutine({ bpId: bp.id }))
    }
  }

  const persistFileTagSelections = nextSelections => {
    if (!bp?.id || !template?.id) return

    const itemTagRequirements = Object.entries(nextSelections).map(
      ([fileTypeId, tagIds]) => ({
        file_type_id: fileTypeId,
        file_tag_ids: (tagIds || []).map(String)
      })
    )

    dispatch(
      updateAttachedAnalyticsTemplateTagRequirementsRoutine({
        bpId: bp.id,
        templateId: template.id,
        item_tag_requirements: itemTagRequirements,
        page: currentPage
      })
    )
  }

  const handleTemplateDetach = e => {
    e.stopPropagation()
    handleOpenRemoveModal(bp, template)
  }

  const handleOverviewClick = e => {
    e.stopPropagation()
    redirect(`${PRIVATE_PATHS.editAnalytics}#${template.id}`)
  }

  const handleToggleOpen = () => {
    setOpen(prev => !prev)
  }

  const provenanceLabel = getTemplateProvenanceLabel(template)

  const dataValues = useMemo(() => {
    let result = []
    const values = pick(
      ['hasUprn', 'hasAddress', 'hasOwner', 'hasAgent', 'hasBuildingTypes'],
      template
    )
    for (const key in values) {
      if (values[key]) {
        result.push(key)
      }
    }
    return result
  }, [template, bp])

  const filesValues = useMemo(() => {
    if (fileCategories && isNotNilOrEmpty(template.fileTypes)) {
      const categories = fileCategories.map(category => {
        return {
          id: category.id,
          label: category.name,
          values:
            template.fileTypes.filter(
              type => type.fileCategoryId === category.id
            ) || []
        }
      })

      return categories.map(category => {
        if (isNotNilOrEmpty(category.values)) {
          return (
            <React.Fragment key={category.id}>
              <CategoryName>{category.label}</CategoryName>
              {category.values.map((value, index) => {
                const filesCount = value?.filesCount || 0
                const checklistItem = checklistFileItemsByType[String(value.id)] || itemOverrides[String(value.id)]
                const displayName = checklistItem?.title || value.name
                const configuredTagNames =
                  getChecklistItemTagNames(checklistItem)
                const legacyConfiguredTagIds =
                  fileItemTagSelections[String(value.id)] || []
                const configuredTagsCount = checklistItem?.id
                  ? configuredTagNames.length
                  : legacyConfiguredTagIds.length
                const leadingContent =
                  configuredTagsCount > 0 ? (
                    checklistItem?.id ? (
                      <ChecklistItemTagsPreviewModal
                        title={`${displayName} tags`}
                        tagNames={configuredTagNames}
                      >
                        <TagPreviewCount>
                          {configuredTagsCount} tag
                          {configuredTagsCount === 1 ? '' : 's'} configured
                        </TagPreviewCount>
                      </ChecklistItemTagsPreviewModal>
                    ) : (
                      <TagPreviewCount>
                        {configuredTagsCount} tag
                        {configuredTagsCount === 1 ? '' : 's'} configured
                      </TagPreviewCount>
                    )
                  ) : null
                const checklistOverview = checklistItem?.checklistOverview || checklistItem?.checklist_overview
                const overviewEntries = checklistOverview?.entries || []
                const tagRequirements = checklistItem?.bp_tag_requirements || checklistItem?.bpTagRequirements || checklistItem?.tag_requirements || checklistItem?.tagRequirements || []
                const tagIdsByGroup = tagRequirements.reduce((acc, req) => {
                  const gNo = Number(req?.group_no ?? req?.groupNo ?? 0)
                  const tagId = req?.file_tag_id || req?.fileTagId || req?.file_tag?.id || req?.fileTag?.id
                  if (!acc[gNo]) acc[gNo] = []
                  if (tagId && !acc[gNo].includes(String(tagId))) acc[gNo].push(String(tagId))
                  return acc
                }, {})
                const uploadedByGroup = overviewEntries.reduce((acc, entry) => {
                  const gNo = Number(entry?.groupNo ?? entry?.group_no ?? 0)
                  if (!acc[gNo]) acc[gNo] = false
                  if (isChecklistEntryUploaded(entry)) {
                    acc[gNo] = true
                  }
                  return acc
                }, {})
                const ruleGroupsMap = {}
                getChecklistGroupNumbers(checklistItem, overviewEntries).forEach(gNo => {
                  const overviewEntry = overviewEntries.find(
                    entry => Number(entry?.groupNo ?? entry?.group_no ?? 0) === gNo
                  )
                  ruleGroupsMap[gNo] = {
                    groupNo: gNo,
                    ruleName: overviewEntry?.ruleName || overviewEntry?.rule_name || '',
                    tagNames: overviewEntry?.tagNames || overviewEntry?.tag_names || [],
                    tagIds: tagIdsByGroup[gNo] || overviewEntry?.tagIds || overviewEntry?.tag_ids || [],
                    assigneeIds: getEntryAssigneeIds(overviewEntry),
                    assigneeUsers: getEntryAssigneeUsers(overviewEntry),
                    disabled: Boolean(uploadedByGroup[gNo])
                  }
                })
                const rulesList = Object.values(ruleGroupsMap)
                const assignedEntries = overviewEntries.filter(
                  e => !!(e?.assignee?.label || e?.assignee?.email)
                )
                const totalEntriesCount = assignedEntries.length
                const uploadedEntriesCount = assignedEntries.filter(
                  e => isChecklistEntryUploaded(e)
                ).length
                const effectiveUploaded = Math.max(uploadedEntriesCount, Math.min(filesCount, totalEntriesCount || overviewEntries.length))
                const effectiveTotal = totalEntriesCount || overviewEntries.length
                const overviewProgress = effectiveTotal > 0
                  ? (effectiveUploaded / effectiveTotal) * 100
                  : filesCount > 0 ? 100 : 0
                const isChecklistItem = Boolean(checklistItem?.id)
                const hasChecklistOverview = overviewEntries.length > 0
                const isChecklistComplete = effectiveTotal > 0
                  ? effectiveUploaded >= effectiveTotal
                  : isChecklistItem && (Boolean(value?.fileExists) || filesCount > 0)
                const canStartChecklistTaskFlow =
                  isTemplateEditable &&
                  !isChecklistComplete &&
                  hasBpTaskManagement(bp)
                const statusChecked = isChecklistItem
                  ? isChecklistComplete
                  : Boolean(value?.fileExists) || overviewProgress >= 100 || filesCount > 0
                const summaryContent = isChecklistItem && hasChecklistOverview ? (
                  <ChecklistOverviewSummary
                    item={checklistItem}
                    leadingContent={leadingContent}
                    filesCount={filesCount}
                    bpId={bp.id}
                    fileTypeId={value.id}
                    categoryId={category.id}
                    templateId={template.id}
                  />
                ) : (
                  <LegacySummaryWrap>
                    {leadingContent}
                    {effectiveTotal > 0 ? (
                      <AlreadyUploadedFilesOfTypeModal
                        bpId={bp.id}
                        type={value.id}
                        categoryId={category.id}
                        templateId={template.id}
                      >
                        <LegacyFilesCount $progress={overviewProgress}>
                          {effectiveUploaded}/{effectiveTotal} file{effectiveTotal === 1 ? '' : 's'} uploaded
                        </LegacyFilesCount>
                      </AlreadyUploadedFilesOfTypeModal>
                    ) : filesCount > 0 ? (
                      <AlreadyUploadedFilesOfTypeModal
                        bpId={bp.id}
                        type={value.id}
                        categoryId={category.id}
                        templateId={template.id}
                      >
                        <LegacyFilesCount $progress={100}>
                          {filesCount}/{filesCount} file{filesCount === 1 ? '' : 's'} uploaded
                        </LegacyFilesCount>
                      </AlreadyUploadedFilesOfTypeModal>
                    ) : null}
                  </LegacySummaryWrap>
                )
                const settingsAction = (
                  <AssignTrigger>
                    <AssignChecklistItemModal
                      bpId={bp.id}
                      templateId={template.id}
                      item={checklistItem || { title: displayName, fileTypeId: value.id, file_type_id: value.id }}
                      itemIdOverride={checklistItem?.id ? undefined : value.id}
                      onAssigned={updatedItem => {
                        if (!updatedItem?.id) return
                        const ftId = updatedItem?.fileTypeId || updatedItem?.file_type_id
                        setItemOverrides(prev => ({
                          ...prev,
                          [updatedItem.id]: updatedItem,
                          ...(ftId ? { [String(ftId)]: updatedItem } : {})
                        }))
                      }}
                    >
                      <SettingsTrigger
                        type='button'
                        aria-label={`Configure ${displayName}`}
                        title='Settings'
                      >
                        <SettingsOutlinedIcon style={{ fontSize: 18 }} />
                        <SettingsLabel>Settings</SettingsLabel>
                      </SettingsTrigger>
                    </AssignChecklistItemModal>
                  </AssignTrigger>
                )
                const addFilesButton = canUploadFiles && !statusChecked ? (
                  rulesList.length > 0 ? (
                    <RulePickerUpload
                      rules={rulesList}
                      bpId={bp.id}
                      categoryId={category.id}
                      typeId={value.id}
                      templateId={template.id}
                      filesCount={value?.filesCount || 0}
                      displayName={displayName}
                      overviewEntries={overviewEntries}
                    />
                  ) : (
                    <AddMultipleFilesModal
                      externalBpId={bp.id}
                      initialCategory={category.id}
                      initialType={value.id}
                      templateId={template.id}
                      addedFilesCount={value?.filesCount || 0}
                    >
                      <AddFilesTrigger
                        type='button'
                        aria-label={`Add files for ${displayName}`}
                        title='Add files'
                      >
                        <NoteAddOutlinedIcon style={{ fontSize: 18 }} />
                        <span>Add files</span>
                      </AddFilesTrigger>
                    </AddMultipleFilesModal>
                  )
                ) : null
                const checklistTaskAction = canStartChecklistTaskFlow ? (
                  <ChecklistTaskTrigger
                    rules={rulesList}
                    bpId={bp.id}
                    bpName={bp.name}
                    categoryId={category.id}
                    typeId={value.id}
                    template={template}
                    item={checklistItem || {
                      id: value.id,
                      title: displayName,
                      fileTypeId: value.id,
                      file_type_id: value.id
                    }}
                    displayName={displayName}
                    overviewEntries={overviewEntries}
                  />
                ) : null
                return filesCount > 0 ? (
                  isTemplateEditable ? (
                    <AlreadyUploadedFilesOfTypeModal
                      key={`${value.id}-${index}`}
                      bpId={bp.id}
                      type={value.id}
                      categoryId={category.id}
                      templateId={template.id}
                    >
                      <FileRow>
                        <SingleItem>
                          <Status checked={statusChecked} $progress={overviewProgress}>
                            {statusChecked ? '☑' : '☐'}
                          </Status>
                          <FileTextColumn>
                            <div>{isChecklistItem ? displayName : `${displayName} (${filesCount} ${filesCount === 1 ? 'file' : 'files'})`}</div>
                            {summaryContent}
                          </FileTextColumn>
                        </SingleItem>
                        <FileItemActions onClick={e => e.stopPropagation()}>
                          {addFilesButton}
                          {settingsAction}
                          {checklistTaskAction}
                        </FileItemActions>
                      </FileRow>
                    </AlreadyUploadedFilesOfTypeModal>
                  ) : (
                    <FileRow key={`${value.id}-${index}`}>
                      <SingleItem>
                        <Status checked={statusChecked} $progress={overviewProgress}>
                          {statusChecked ? '☑' : '☐'}
                        </Status>
                        <FileTextColumn>
                          <div>{isChecklistItem ? displayName : `${displayName} (${filesCount} ${filesCount === 1 ? 'file' : 'files'})`}</div>
                          {summaryContent}
                        </FileTextColumn>
                      </SingleItem>
                      <FileItemActions onClick={e => e.stopPropagation()}>
                        {addFilesButton}
                        {settingsAction}
                        {checklistTaskAction}
                      </FileItemActions>
                    </FileRow>
                  )
                ) : (
                  <FileItemWrapper key={`${category.label}-${index}`}>
                    <FileRow>
                      <SingleItem>
                        <Status checked={statusChecked} $progress={overviewProgress}>
                          {statusChecked ? '☑' : '☐'}
                        </Status>
                        <FileTextColumn>
                          <div>{isChecklistItem ? displayName : `${displayName} (${filesCount} ${filesCount === 1 ? 'file' : 'files'})`}</div>
                          {summaryContent}
                        </FileTextColumn>
                      </SingleItem>
                      <FileItemActions onClick={e => e.stopPropagation()}>
                        {addFilesButton}
                        {settingsAction}
                        {checklistTaskAction}
                      </FileItemActions>
                    </FileRow>
                  </FileItemWrapper>
                )
              })}
            </React.Fragment>
          )
        } else {
          return null
        }
      }, [])
    }
  }, [
    template,
    fileCategories,
    bp,
    bpFiles,
    fileItemTagSelections,
    checklistFileItemsByType
  ])

  const closeEditModal = () => {
    setEditContentName('')
    setModalOpen(false)
  }

  const openEditModal = name => () => {
    setEditContentName(name)
    setModalOpen(true)
  }

  const modalContent = useMemo(() => {
    switch (editContentName) {
      case 'hasUprn':
        return (
          <EditGeneralInfo
            isAnalyticsView
            currentPage={currentPage}
            closeModal={closeEditModal}
          />
        )
      case 'hasAddress':
        return (
          <EditAddress
            isAnalyticsView
            currentPage={currentPage}
            closeModal={closeEditModal}
          />
        )
      case 'hasBuildingTypes':
        return (
          <EditBuildingTypes
            isAnalyticsView
            currentPage={currentPage}
            closeModal={closeEditModal}
          />
        )
      case 'hasAgent':
        return (
          <AddAssignmentModal
            isAnalyticsView
            currentPage={currentPage}
            initialUserType='agent'
            bp={bp}
            closeModal={closeEditModal}
          />
        )
      case 'hasOwner':
        return (
          <AddAssignmentModal
            isAnalyticsView
            currentPage={currentPage}
            initialUserType='owner'
            bp={bp}
            closeModal={closeEditModal}
          />
        )
      default:
        return <div />
    }
  }, [editContentName])

  const contentBody = (
    <DetailsWrapper
      open={open || showDetailsOnly}
      $showDetailsOnly={showDetailsOnly} // Prop styled custom
    >
      <SectionTitle>data</SectionTitle>
      {dataValues.map(item => {
        return (
          <SingleItem
            key={item}
            onClick={isTemplateEditable ? openEditModal(item) : () => {}}
          >
            <Status checked={checkAnalyticsCondition(item, bp, bpFiles)}>
              {checkAnalyticsCondition(item, bp, bpFiles) ? '☑' : '☐'}
            </Status>
            {TEMPLATE_ITEMS_NAMES[item]}
          </SingleItem>
        )
      })}
      {isNotNilOrEmpty(template.fileTypes) && (
        <>
          <FileSectionTitle>files</FileSectionTitle>
          {filesValues}
        </>
      )}
    </DetailsWrapper>
  )

  return (
    <Wrapper isDetailsView={isDetailsView}>
      {!showDetailsOnly && (
        <ItemWrapper onClick={handleToggleOpen}>
          <TemplateNameWrapper>
            <TemplateName>{template.name}</TemplateName>
            <TemplateMetaBlock>
              <TemplateMetaInline>
                {template.visibility && (
                  <MetaInlineItem
                    $accent={
                      template.visibility === 'personal'
                        ? 'personal'
                        : 'default'
                    }
                  >
                    {template.visibility === 'personal' ? (
                      <PersonIcon fontSize='inherit' />
                    ) : (
                      <PeopleIcon fontSize='inherit' />
                    )}
                    <span>
                      {template.visibility === 'personal'
                        ? 'Personal'
                        : 'Shared'}
                    </span>
                  </MetaInlineItem>
                )}
                {hasLockState && (!showOverviewButton || isLocked) && (
                  <MetaInlineItem $accent={isLocked ? 'locked' : 'default'}>
                    {isLocked ? (
                      <EditOffIcon fontSize='inherit' />
                    ) : (
                      <EditIcon fontSize='inherit' />
                    )}
                    <span>{isLocked ? 'Non-editable' : 'Editable'}</span>
                  </MetaInlineItem>
                )}
                {showOverviewButton &&
                  isDetailsView &&
                  !isLocked &&
                  canEditTemplate && (
                    <OverviewBadge type='button' onClick={handleOverviewClick}>
                      <LaunchIcon fontSize='inherit' />
                      <span>Edit template</span>
                    </OverviewBadge>
                  )}
                {provenanceLabel && (
                  <Tooltip title={provenanceLabel} arrow>
                    <MetaInfoIcon
                      aria-label={provenanceLabel}
                      onClick={event => event.stopPropagation()}
                    >
                      <InfoOutlinedIcon fontSize='inherit' />
                    </MetaInfoIcon>
                  </Tooltip>
                )}
              </TemplateMetaInline>
            </TemplateMetaBlock>
          </TemplateNameWrapper>
          <ProgressBarWrapper>
            <Percentage>{templatePercentage}%</Percentage>
            <ProgressBar width={templatePercentage} />
          </ProgressBarWrapper>
          <ActionsWrapper>
            {!isDetailsView && (
              <CrossIcon
                disabled={isDefaultTemplate || !canDetachTemplate}
                onClick={
                  isDefaultTemplate || !canDetachTemplate
                    ? () => {}
                    : handleTemplateDetach
                }
              />
            )}
            <CollapseIcon
              onClick={event => {
                event.stopPropagation()
                handleToggleOpen()
              }}
              open={open}
              isDetailsView={isDetailsView}
            />
          </ActionsWrapper>
        </ItemWrapper>
      )}

      {showDetailsOnly ? (
        contentBody
      ) : (
        <Collapse in={open}>{contentBody}</Collapse>
      )}

      <Modal open={modalOpen} title='Edit' onClose={closeEditModal}>
        {modalContent}
      </Modal>
    </Wrapper>
  )
}

export default AnalyticProgressBar

const Wrapper = styled.div`
  margin: 7px 0;

  ${({ isDetailsView }) =>
    isDetailsView &&
    css`
      &:not(:last-of-type) {
        margin-bottom: 20px;
      }
    `}
`

const ItemWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
`

const TemplateNameWrapper = styled.div`
  margin-right: 20px;
  width: 25%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const TemplateName = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 1400px) {
    font-size: 15px;
  }
`

const ProgressBarWrapper = styled.div`
  width: 75%;
  position: relative;
  padding-right: 56px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.backgroundColor};
  border-radius: 4px;
`

const ProgressBar = styled.div`
  width: ${({ width }) => width}%;
  height: 5px;
  background-color: ${({ width }) => color(width)};
  border-radius: 4px;
`

const Percentage = styled.div`
  position: absolute;
  right: 0;
  font-size: 12px;
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
`

const DetailsWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.grey[400]};
  padding: 20px;
  transition: all 0.3s;
  margin: ${({ open }) => (open ? '10px 0' : 0)};

  ${({ $showDetailsOnly }) =>
    $showDetailsOnly &&
    css`
      background-color: transparent;
      padding: 0;
      margin: 0;
    `}
`

const SingleItem = styled.div`
  margin-bottom: 5px;
  font-size: 14px;
  display: flex;
  align-items: center;
  cursor: pointer;
`

const FileTextColumn = styled.div`
  min-width: 0;
`

const TagPreviewCount = styled.div`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 12px;
  background: rgba(51, 190, 204, 0.14);
  color: #156f78;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
`

const LegacySummaryWrap = styled.div`
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
`

const LegacyFilesCount = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 12px;
  background: ${({ $progress = 0 }) => getProgressBadgeColors($progress).bg};
  color: ${({ $progress = 0 }) => getProgressBadgeColors($progress).color};
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
`

const SectionTitle = styled.div`
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 10px;
  font-weight: 600;
`

const CollapseIcon = styled(KeyboardArrowDownOutlinedIcon)`
  transition: all 0.3s;
  cursor: pointer;
  transform: rotate(${({ open }) => (open ? '180deg' : '0')});
  margin-left: ${({ isDetailsView }) => (isDetailsView ? '15px' : 0)};
`

const FileSectionTitle = styled.div`
  text-transform: uppercase;
  margin: 30px 0 10px;
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 600;
`

const CategoryName = styled.div`
  color: ${({ theme }) => theme.colors.secondary};
  margin: 15px 0 10px;
`

const getStatusColor = (checked, progress) => {
  if (typeof progress === 'number') {
    if (progress >= 100) return '#208553'
    if (progress > 0) return '#b27300'
    return '#b00020'
  }
  return checked ? '#33becc' : '#b00020'
}

const getProgressBadgeColors = progress => {
  if (progress >= 100) return { bg: 'rgba(61, 179, 122, 0.14)', color: '#208553' }
  if (progress > 0) return { bg: 'rgba(255, 170, 0, 0.14)', color: '#b27300' }
  return { bg: 'transparent', color: '#b00020' }
}

const Status = styled.div`
  color: ${({ checked, $progress }) => getStatusColor(checked, $progress)};
  font-size: 20px;
  font-weight: bold;
  margin-right: 10px;
  padding-bottom: 2px;
`

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const OverviewBadge = styled.button`
  border: none;
  background: rgba(31, 108, 153, 0.12);
  color: #1f6c99;
  padding: 3px 8px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  line-height: 1.2;
  white-space: nowrap;

  &:hover {
    background: rgba(31, 108, 153, 0.2);
    color: #15557b;
  }

  svg {
    font-size: 12px;
  }
`

const CrossIcon = styled(ClearIcon)`
  margin: 0 10px;
  font-size: 14px !important;
  cursor: pointer;
  color: ${({ disabled, theme }) =>
    disabled ? theme.colors.grey[600] : theme.colors.primary};
`
const FileItemWrapper = styled.div`
  display: block;
  width: 100%;
  margin-bottom: 5px;

  & > div {
    width: 100%;
  }
`

const FileRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    align-items: flex-start;
    gap: 8px;
  }
`

const FileItemActions = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 24px;
  align-self: flex-start;
  margin-top: 4px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
    margin-top: 0;
  }
`

const TagTrigger = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const AssignTrigger = styled.div`
  display: inline-flex;
  align-items: center;
`

const AddFilesTrigger = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;
  flex: 0 0 28px;

  &:hover {
    background: rgba(51, 190, 204, 0.1);
  }

  span {
    display: none;
  }
`

const CreateTaskTrigger = styled(AddFilesTrigger)`
  color: #208553;

  &:hover {
    background: rgba(61, 179, 122, 0.1);
    color: #16643f;
  }
`

const BackButton = styled.button`
  border: none;
  background: none;
  color: #17818a;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`

const RulePickerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const RulePickerHint = styled.div`
  font-size: 13px;
  color: #6f7785;
  line-height: 1.5;
`

const RulePickerCard = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid #ebedf1;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #33becc;
    background: rgba(51, 190, 204, 0.04);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
    background: #f8fafc;
  }
`

const RulePickerIndex = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(51, 190, 204, 0.12);
  color: #156f78;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const RulePickerInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const RulePickerName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #172033;
`

const RulePickerTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`

const RulePickerTag = styled.span`
  padding: 3px 8px;
  border-radius: 999px;
  background: #f3f6fb;
  color: #33415d;
  font-size: 11px;
  font-weight: 600;
`

const RulePickerState = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #6f7785;
`

const RuleUploadedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 8px;
  background: rgba(61, 179, 122, 0.14);
  color: #208553;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
`

const RuleUploadedIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 6px 10px;
  background: rgba(61, 179, 122, 0.14);
  color: #208553;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
`

const SettingsTrigger = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;
  flex: 0 0 28px;

  &:hover {
    background: rgba(51, 190, 204, 0.1);
  }
`

const SettingsLabel = styled.span`
  display: none;
`

const CreateTaskIcon = styled(AddTaskOutlinedIcon)`
  font-size: 18px !important;
  color: currentColor;
`

const TemplateMetaInline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  min-height: 22px;
  min-width: 0;
`

const TemplateMetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const MetaInlineItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 1.2;
  white-space: nowrap;
  padding: 3px 8px;
  border-radius: 999px;
  background: ${({ $accent }) => {
    if ($accent === 'locked') return 'rgba(180, 63, 45, 0.12)'
    if ($accent === 'personal') return 'rgba(31, 108, 153, 0.12)'
    return 'rgba(79, 90, 106, 0.1)'
  }};
  color: ${({ $accent }) => {
    if ($accent === 'locked') return '#b43f2d'
    if ($accent === 'personal') return '#1f6c99'
    return '#4f5a6a'
  }};

  svg {
    font-size: 12px;
    opacity: 0.95;
  }

  @media (max-width: 1280px) {
    font-size: 11px;
  }
`

const MetaInfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: #5f6b7a;
  width: 20px;
  height: 20px;
  justify-content: center;
  border-radius: 50%;
  background: rgba(95, 107, 122, 0.12);
  cursor: help;

  svg {
    font-size: 14px;
    opacity: 0.85;
  }
`
